import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash, Image as ImageIcon, Palette, Layout, ArrowsDownUp } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

const POSITIONS = [
  { value: "left", label: "Izquierda" },
  { value: "center", label: "Centro" },
  { value: "right", label: "Derecha" },
];

const LOCATIONS = [
  { value: "header", label: "Encabezado" },
  { value: "footer", label: "Pie de pagina" },
  { value: "both", label: "Ambos" },
];

const BANNER_POSITIONS = [
  { value: "start", label: "Inicio (despues del header)" },
  { value: "middle", label: "Medio (entre secciones)" },
  { value: "end", label: "Final (antes del footer)" },
];

const TEMPLATES = [
  { value: "basica", label: "Basica", desc: "Logo izquierdo + info derecha" },
  { value: "intermedia", label: "Intermedia", desc: "3 columnas: logo izq. | nombre centro | logo der." },
];

const COLOR_PRESETS = [
  { primary: "#1E40AF", bg: "#1E40AF", label: "Azul corporativo" },
  { primary: "#0F766E", bg: "#0F766E", label: "Verde teal" },
  { primary: "#7C3AED", bg: "#7C3AED", label: "Violeta" },
  { primary: "#DC2626", bg: "#DC2626", label: "Rojo" },
  { primary: "#0A2463", bg: "#0A2463", label: "Azul oscuro" },
  { primary: "#B45309", bg: "#B45309", label: "Naranja" },
];

// =================== LOGO CARD ===================
const LogoCard = ({ logo, onDelete }) => {
  const posLabel = POSITIONS.find(p => p.value === logo.position)?.label || logo.position;
  const locLabel = LOCATIONS.find(l => l.value === logo.location)?.label || logo.location;
  return (
    <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg" data-testid={`logo-card-${logo.id}`}>
      <div className="w-16 h-12 bg-slate-100 rounded flex items-center justify-center overflow-hidden shrink-0">
        <img src={`${API}/reports/media/${logo.path.replace("reports/", "")}`} alt={logo.filename} className="max-w-full max-h-full object-contain" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{logo.filename}</p>
        <p className="text-xs text-slate-500">{posLabel} &middot; {locLabel}</p>
      </div>
      <button onClick={() => onDelete(logo.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" data-testid={`delete-logo-${logo.id}`}>
        <Trash size={16} />
      </button>
    </div>
  );
};

// =================== BANNER CARD ===================
const BannerCard = ({ banner, onDelete }) => {
  const posLabel = BANNER_POSITIONS.find(p => p.value === banner.position)?.label || banner.position;
  return (
    <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg" data-testid={`banner-card-${banner.id}`}>
      <div className="w-20 h-10 bg-slate-100 rounded flex items-center justify-center overflow-hidden shrink-0">
        <img src={`${API}/reports/media/${banner.path.replace("reports/", "")}`} alt={banner.filename} className="max-w-full max-h-full object-contain" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{banner.filename}</p>
        <p className="text-xs text-slate-500">{posLabel}</p>
      </div>
      <button onClick={() => onDelete(banner.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" data-testid={`delete-banner-${banner.id}`}>
        <Trash size={16} />
      </button>
    </div>
  );
};

// =================== UPLOAD BUTTON ===================
const UploadArea = ({ onUpload, category, uploading, label }) => {
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten imagenes");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Maximo 5MB por imagen");
      return;
    }
    await onUpload(file, category);
    e.target.value = "";
  };

  return (
    <label className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors" data-testid={`upload-${category}`}>
      <Upload size={18} className="text-slate-500" />
      <span className="text-sm text-slate-600">{uploading ? "Subiendo..." : label}</span>
      <input type="file" accept="image/*" onChange={handleFile} className="hidden" disabled={uploading} />
    </label>
  );
};

// =================== MAIN CONFIG COMPONENT ===================
const ReportsConfig = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newLogoPos, setNewLogoPos] = useState("left");
  const [newLogoLoc, setNewLogoLoc] = useState("header");
  const [newBannerPos, setNewBannerPos] = useState("start");

  const headers = { Authorization: `Bearer ${token}` };

  const loadConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reports/config`, { headers });
      setConfig(res.data);
    } catch {
      toast.error("Error al cargar configuracion");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async (updates) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/reports/config`, updates, { headers });
      setConfig(res.data);
      toast.success("Configuracion guardada");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file, category) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/reports/upload?category=${category}`, fd, { headers });
      const { path, filename } = res.data;

      if (category === "logos") {
        const newLogo = { path, filename, position: newLogoPos, location: newLogoLoc };
        await saveConfig({ logos: [...(config?.logos || []), newLogo] });
      } else {
        const newBanner = { path, filename, position: newBannerPos };
        await saveConfig({ banners: [...(config?.banners || []), newBanner] });
      }
    } catch {
      toast.error("Error al subir archivo");
    } finally {
      setUploading(false);
    }
  };

  const deleteLogo = async (id) => {
    const updated = (config?.logos || []).filter(l => l.id !== id);
    await saveConfig({ logos: updated });
  };

  const deleteBanner = async (id) => {
    const updated = (config?.banners || []).filter(b => b.id !== id);
    await saveConfig({ banners: updated });
  };

  if (loading) return <div className="text-center text-slate-500 py-10">Cargando configuracion...</div>;
  if (!config) return <div className="text-center text-red-500 py-10">Error al cargar</div>;

  return (
    <div className="space-y-8" data-testid="reports-config">
      {/* TEMPLATE SELECTOR */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Layout size={20} className="text-blue-600" weight="duotone" />
          <h3 className="font-semibold text-slate-800">Plantilla de Reporte</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TEMPLATES.map(t => (
            <button key={t.value} onClick={() => saveConfig({ template: t.value })}
              data-testid={`template-${t.value}`}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                config.template === t.value
                  ? "border-blue-500 bg-blue-50/70 ring-1 ring-blue-200"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}>
              <p className="font-medium text-sm text-slate-800">{t.label}</p>
              <p className="text-xs text-slate-500 mt-1">{t.desc}</p>
            </button>
          ))}
        </div>
      </section>

      {/* COLOR SETTINGS */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Palette size={20} className="text-blue-600" weight="duotone" />
          <h3 className="font-semibold text-slate-800">Colores del Reporte</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {COLOR_PRESETS.map((cp, i) => (
            <button key={i} onClick={() => saveConfig({ primary_color: cp.primary, header_bg: cp.bg })}
              data-testid={`color-preset-${i}`}
              className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                config.primary_color === cp.primary
                  ? "border-blue-500 bg-blue-50/50"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}>
              <div className="w-5 h-5 rounded-full shrink-0" style={{ backgroundColor: cp.primary }} />
              <span className="text-xs text-slate-700 truncate">{cp.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Primario:</Label>
            <input type="color" value={config.primary_color || "#1E40AF"} onChange={e => saveConfig({ primary_color: e.target.value })}
              className="w-8 h-8 rounded cursor-pointer border-0" data-testid="color-primary-input" />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Header BG:</Label>
            <input type="color" value={config.header_bg || "#1E40AF"} onChange={e => saveConfig({ header_bg: e.target.value })}
              className="w-8 h-8 rounded cursor-pointer border-0" data-testid="color-headerbg-input" />
          </div>
        </div>
      </section>

      {/* LOGOS */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon size={20} className="text-blue-600" weight="duotone" />
          <h3 className="font-semibold text-slate-800">Logos</h3>
          <span className="text-xs text-slate-400 ml-1">(max 6)</span>
        </div>
        {(config.logos || []).length > 0 && (
          <div className="space-y-2 mb-4">
            {config.logos.map(l => <LogoCard key={l.id} logo={l} onDelete={deleteLogo} />)}
          </div>
        )}
        {(config.logos || []).length < 6 && (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs text-slate-500">Posicion</Label>
              <Select value={newLogoPos} onValueChange={setNewLogoPos}>
                <SelectTrigger className="w-32 h-9 mt-1" data-testid="new-logo-position"><SelectValue /></SelectTrigger>
                <SelectContent>{POSITIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Ubicacion</Label>
              <Select value={newLogoLoc} onValueChange={setNewLogoLoc}>
                <SelectTrigger className="w-36 h-9 mt-1" data-testid="new-logo-location"><SelectValue /></SelectTrigger>
                <SelectContent>{LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <UploadArea onUpload={handleUpload} category="logos" uploading={uploading} label="Subir logo" />
          </div>
        )}
      </section>

      {/* BANNERS */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <ArrowsDownUp size={20} className="text-blue-600" weight="duotone" />
          <h3 className="font-semibold text-slate-800">Banners</h3>
          <span className="text-xs text-slate-400 ml-1">(max 6)</span>
        </div>
        {(config.banners || []).length > 0 && (
          <div className="space-y-2 mb-4">
            {config.banners.map(b => <BannerCard key={b.id} banner={b} onDelete={deleteBanner} />)}
          </div>
        )}
        {(config.banners || []).length < 6 && (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs text-slate-500">Posicion en PDF</Label>
              <Select value={newBannerPos} onValueChange={setNewBannerPos}>
                <SelectTrigger className="w-56 h-9 mt-1" data-testid="new-banner-position"><SelectValue /></SelectTrigger>
                <SelectContent>{BANNER_POSITIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <UploadArea onUpload={handleUpload} category="banners" uploading={uploading} label="Subir banner" />
          </div>
        )}
      </section>

      {/* PREVIEW HINT */}
      <div className="border border-dashed border-slate-300 rounded-lg p-5 bg-slate-50/60 text-center">
        <p className="text-sm text-slate-600 font-medium">Vista Previa</p>
        <p className="text-xs text-slate-400 mt-1">Genera cualquier reporte PDF para visualizar los cambios aplicados.</p>
        <div className="mt-4 inline-block border border-slate-200 rounded bg-white p-4 w-64">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-6 rounded bg-slate-200 flex items-center justify-center text-[8px] text-slate-400">
              {config.logos?.find(l => l.position === "left") ? "L" : ""}
            </div>
            <div className="text-[10px] font-bold" style={{ color: config.primary_color || "#1E40AF" }}>
              {config.template === "intermedia" ? "EMPRESA" : ""}
            </div>
            <div className="w-8 h-6 rounded bg-slate-200 flex items-center justify-center text-[8px] text-slate-400">
              {config.logos?.find(l => l.position === "right") ? "R" : ""}
            </div>
          </div>
          <div className="h-1 rounded-full mb-2" style={{ backgroundColor: config.header_bg || "#1E40AF" }} />
          {config.banners?.some(b => b.position === "start") && (
            <div className="h-3 bg-slate-100 rounded mb-1 text-[6px] text-slate-400 text-center">Banner inicio</div>
          )}
          <div className="space-y-1">
            <div className="h-2 bg-slate-100 rounded w-3/4" />
            <div className="h-2 bg-slate-100 rounded w-full" />
            <div className="h-2 bg-slate-100 rounded w-2/3" />
          </div>
          {config.banners?.some(b => b.position === "middle") && (
            <div className="h-3 bg-slate-100 rounded my-1 text-[6px] text-slate-400 text-center">Banner medio</div>
          )}
          <div className="space-y-1 mt-1">
            <div className="h-2 bg-slate-100 rounded w-full" />
            <div className="h-2 bg-slate-100 rounded w-1/2" />
          </div>
          {config.banners?.some(b => b.position === "end") && (
            <div className="h-3 bg-slate-100 rounded mt-1 text-[6px] text-slate-400 text-center">Banner final</div>
          )}
          <div className="h-0.5 rounded-full mt-2" style={{ backgroundColor: config.header_bg || "#1E40AF", opacity: 0.5 }} />
        </div>
      </div>
    </div>
  );
};

export { ReportsConfig };
